import React, { useCallback, useState, useEffect, useMemo } from "react"
import type { Node, Edge, Connection, NodeTypes } from "reactflow"
import ReactFlow, { Controls, Background, useNodesState, useEdgesState, addEdge, MiniMap } from "reactflow"
import "reactflow/dist/style.css"
import { trpc } from "../../lib/trpc"
import { Loader2 } from "lucide-react"
import TableDrawer from "./components/TableDrawer"
import TableNode from "./components/TableNode"
import SearchBar from "./components/SearchBar"

interface GraphTabProps {
  selectedSystemId: string | null
}

const GraphTab: React.FC<GraphTabProps> = ({ selectedSystemId }) => {
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null)
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Feature II: Search state
  const [searchQuery, setSearchQuery] = useState("")
  const [matchingTableIds, setMatchingTableIds] = useState<string[]>([])

  // Feature I & III: Hover/highlight state
  const [hoveredRelationships, setHoveredRelationships] = useState<string[]>([])
  const [hoveredNodes, setHoveredNodes] = useState<string[]>([])
  const [activeBadgeTableId, setActiveBadgeTableId] = useState<string | null>(null)

  // Fetch graph data (now includes columns in bulk)
  const { data: graphData, isLoading } = trpc.databrain.getGraph.useQuery({
    systemId: selectedSystemId || undefined,
  })

  // Define custom node types
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      tableNode: TableNode,
    }),
    [],
  )

  // Feature I: Column to table ID mapping for relationships
  const columnToTableMap = useMemo(() => {
    const map = new Map<string, string>()
    graphData?.tables.forEach((table) => {
      table.columns?.forEach((col) => {
        map.set(col.id, table.id)
      })
    })
    return map
  }, [graphData])

  // Feature I: Relationship lookup by column ID
  const relationshipLookup = useMemo(() => {
    const lookup = new Map<string, string[]>()
    graphData?.relationships.forEach((rel) => {
      const fromIds = lookup.get(rel.fromColumnId) || []
      const toIds = lookup.get(rel.toColumnId) || []
      lookup.set(rel.fromColumnId, [...fromIds, rel.id])
      lookup.set(rel.toColumnId, [...toIds, rel.id])
    })
    return lookup
  }, [graphData])

  // Feature III: Relationship count per table
  const tableRelationshipCounts = useMemo(() => {
    const counts = new Map<string, number>()
    graphData?.tables.forEach((table) => {
      let count = 0
      table.columns?.forEach((col) => {
        const rels = graphData.relationships.filter((rel) => rel.fromColumnId === col.id || rel.toColumnId === col.id)
        count += rels.length
      })
      counts.set(table.id, count)
    })
    return counts
  }, [graphData])

  // Feature II: Search filter logic
  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchingTableIds([])
      return
    }

    const query = searchQuery.toLowerCase()
    const matches =
      graphData?.tables
        .filter((table) => {
          const tableMatch = table.name.toLowerCase().includes(query)
          const schemaMatch = table.schema.toLowerCase().includes(query)
          const descMatch = table.description?.toLowerCase().includes(query)
          const columnMatch = table.columns?.some((col) => col.name.toLowerCase().includes(query))
          return tableMatch || schemaMatch || descMatch || columnMatch
        })
        .map((t) => t.id) || []

    setMatchingTableIds(matches)
  }, [searchQuery, graphData])

  // Feature I: Column hover handler
  const handleColumnHover = useCallback(
    (columnId: string | null, columnRelationshipIds: string[]) => {
      if (!columnId) {
        setHoveredRelationships([])
        setHoveredNodes([])
        return
      }

      setHoveredRelationships(columnRelationshipIds)

      // Find all tables involved in these relationships
      const involvedTableIds = new Set<string>()
      columnRelationshipIds.forEach((relId) => {
        const rel = graphData?.relationships.find((r) => r.id === relId)
        if (rel) {
          const fromTable = columnToTableMap.get(rel.fromColumnId)
          const toTable = columnToTableMap.get(rel.toColumnId)
          if (fromTable) involvedTableIds.add(fromTable)
          if (toTable) involvedTableIds.add(toTable)
        }
      })
      setHoveredNodes(Array.from(involvedTableIds))
    },
    [graphData, columnToTableMap],
  )

  // Feature III: Badge click handler
  const handleBadgeClick = useCallback(
    (tableId: string) => {
      // Toggle selection
      if (activeBadgeTableId === tableId) {
        setActiveBadgeTableId(null)
        setHoveredRelationships([])
        setHoveredNodes([])
        return
      }

      setActiveBadgeTableId(tableId)

      // Find all relationships for this table
      const table = graphData?.tables.find((t) => t.id === tableId)
      if (!table) return

      const columnIds = table.columns?.map((c) => c.id) || []
      const relIds =
        graphData?.relationships
          .filter((rel) => columnIds.includes(rel.fromColumnId) || columnIds.includes(rel.toColumnId))
          .map((r) => r.id) || []

      setHoveredRelationships(relIds)

      // Find all connected tables
      const connectedTableIds = new Set<string>()
      relIds.forEach((relId) => {
        const rel = graphData?.relationships.find((r) => r.id === relId)
        if (rel) {
          const fromTable = columnToTableMap.get(rel.fromColumnId)
          const toTable = columnToTableMap.get(rel.toColumnId)
          if (fromTable) connectedTableIds.add(fromTable)
          if (toTable) connectedTableIds.add(toTable)
        }
      })
      setHoveredNodes(Array.from(connectedTableIds))
    },
    [activeBadgeTableId, graphData, columnToTableMap],
  )

  // Feature I: Edge hover handlers
  const onEdgeMouseEnter = useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      setHoveredRelationships([edge.id])

      // Find tables involved
      const rel = graphData?.relationships.find((r) => r.id === edge.id)
      if (rel) {
        const fromTable = columnToTableMap.get(rel.fromColumnId)
        const toTable = columnToTableMap.get(rel.toColumnId)
        setHoveredNodes([fromTable, toTable].filter(Boolean))
      }
    },
    [graphData, columnToTableMap],
  )

  const onEdgeMouseLeave = useCallback(() => {
    // Don't clear if badge is active
    if (!activeBadgeTableId) {
      setHoveredRelationships([])
      setHoveredNodes([])
    }
  }, [activeBadgeTableId])

  // Click canvas to deselect badge
  const handlePaneClick = useCallback(() => {
    setActiveBadgeTableId(null)
    setHoveredRelationships([])
    setHoveredNodes([])
  }, [])

  // Update nodes and edges when data changes
  useEffect(() => {
    if (graphData) {
      // Determine search/highlight state
      const isSearchActive = searchQuery.trim().length > 0

      // Convert tables to nodes with column data
      const graphNodes: Node[] = graphData.tables.map((table, index) => {
        const isMatch = matchingTableIds.length === 0 || matchingTableIds.includes(table.id)
        const isHovered = hoveredNodes.includes(table.id)

        return {
          id: table.id,
          type: "tableNode",
          position: { x: 100 + (index % 4) * 350, y: 100 + Math.floor(index / 4) * 300 },
          data: {
            id: table.id,
            name: table.name,
            schema: table.schema,
            description: table.description,
            columns: table.columns || [],
            onViewDetails: (tableId: string) => setSelectedTableId(tableId),
            // Feature I: Column hover
            onColumnHover: handleColumnHover,
            relationshipLookup,
            // Feature III: Relationship badge
            relationshipCount: tableRelationshipCounts.get(table.id) || 0,
            onBadgeClick: handleBadgeClick,
            isBadgeActive: activeBadgeTableId === table.id,
          },
          style: {
            opacity: isSearchActive && !isMatch ? 0.3 : hoveredNodes.length > 0 && !isHovered ? 0.3 : 1,
            transition: "opacity 0.2s ease",
          },
          className: (isMatch && isSearchActive) || isHovered ? "ring-2 ring-primary" : "",
        }
      })

      // Convert relationships to edges with hover effects
      const graphEdges: Edge[] = graphData.relationships.map((rel) => {
        const isHovered = hoveredRelationships.includes(rel.id)

        return {
          id: rel.id,
          source: columnToTableMap.get(rel.fromColumnId) || rel.fromColumnId,
          target: columnToTableMap.get(rel.toColumnId) || rel.toColumnId,
          type: "smoothstep",
          animated: isHovered || rel.type === "inferred",
          label: rel.canonicalName || undefined,
          style: {
            stroke: isHovered
              ? "hsl(var(--primary))"
              : rel.isCanonical
                ? "hsl(var(--primary))"
                : "hsl(var(--muted-foreground))",
            strokeWidth: isHovered ? 3 : rel.isCanonical ? 2 : 1,
            opacity: hoveredRelationships.length > 0 && !isHovered ? 0.2 : 1,
            transition: "all 0.2s ease",
          },
          labelStyle: {
            fontSize: 10,
            fill: "hsl(var(--foreground))",
          },
          labelBgStyle: {
            fill: "hsl(var(--background))",
            opacity: 0.8,
          },
        }
      })

      setNodes(graphNodes)
      setEdges(graphEdges)
    }
  }, [
    graphData,
    searchQuery,
    matchingTableIds,
    hoveredNodes,
    hoveredRelationships,
    activeBadgeTableId,
    columnToTableMap,
    relationshipLookup,
    tableRelationshipCounts,
    handleColumnHover,
    handleBadgeClick,
    setNodes,
    setEdges,
  ])

  const onConnect = useCallback((params: Connection) => setEdges((eds) => addEdge(params, eds)), [setEdges])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="relative h-full">
      {/* Feature II: Search Bar */}
      {!isLoading && nodes.length > 0 && (
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          matchCount={matchingTableIds.length}
          totalCount={graphData?.tables.length || 0}
        />
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onPaneClick={handlePaneClick}
        fitView
        minZoom={0.1}
        maxZoom={2}
      >
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            return "hsl(var(--primary))"
          }}
          maskColor="hsl(var(--background) / 0.8)"
        />
        <Background />
      </ReactFlow>

      {/* Table Drawer */}
      {selectedTableId && (
        <TableDrawer tableId={selectedTableId} isOpen={!!selectedTableId} onClose={() => setSelectedTableId(null)} />
      )}

      {/* Empty State */}
      {!isLoading && nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-muted-foreground">No tables found. Import tables first.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default GraphTab
